import {
  Avatar,
  BorderMenuList,
  Button,
  Flexbox,
  Modal,
  ModalBody,
  ModalFooter,
  OverlayPortal,
  StyledMenu,
  StyledMenuButton,
  Text,
  Theme,
  color,
} from '@morressier/ts';
import { meetingAction, ScheduledItemStatus, ScheduledMeeting } from 'api/events';
import { FaEllipsisH } from 'react-icons/fa';
import CalendarExportDropdown from 'containers/SingleSession/CalendarExportDropdown';
import { useAuthContext } from 'store';
import { MenuItem } from '@reach/menu-button';
import { SessionListCard } from 'containers/SessionsList/styles';
import { getTimeText } from 'utils/timeAndDateHelper';
import { useEffect, useReducer, useRef, useState } from 'react';
import { addHours } from 'date-fns';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { logEvent } from 'utils/eventTracking';

export const CancellationModal: React.FC<{
  cancelMeeting: () => void;
  toggleCancellationModal: () => void;
}> = ({ cancelMeeting, toggleCancellationModal }) => (
  <OverlayPortal>
    <Modal copies={{ title: 'Cancel Meeting' }} close={toggleCancellationModal}>
      <ModalBody>
        <Text>
          Are you sure you want to cancel this meeting? We will notify everyone involved to let them
          know.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Flexbox gap="1rem" justifyContent="flex-end" style={{ width: '100%' }}>
          <Button onClick={toggleCancellationModal}>Cancel</Button>
          <Button
            color="danger"
            onClick={() => {
              cancelMeeting();
              toggleCancellationModal();
            }}
          >
            Cancel Meeting
          </Button>
        </Flexbox>
      </ModalFooter>
    </Modal>
  </OverlayPortal>
);

const DeclineButton = styled(Text)`
  &:hover {
    text-decoration: underline;
  }
`;

const Flyout = styled(BorderMenuList)`
  box-shadow: 0 4px 4px 0 rgba(66, 77, 93, 0.16);
`;

const MoreButton = styled(FaEllipsisH)`
  color: ${color('grey')};

  &:hover {
    color: ${color('secondaryBrand')};
  }
`;

interface Props {
  meeting: ScheduledMeeting;
  mutate: () => void;
  event: MorressierEvent;
}

const trackingEventPerMeetingAction = {
  [ScheduledItemStatus.Accepted]: 'ACCEPT',
  [ScheduledItemStatus.Rejected]: 'DECLINE',
  [ScheduledItemStatus.Canceled]: 'CANCEL',
  [ScheduledItemStatus.Deleted]: 'DELETE',
};

const generateVideoCallUrl = (userOneId: string, userTwoId: string): string => {
  const hostname =
    process.env.NODE_ENV === 'development' ? 'https://next.morressier.com' : window.location.origin;
  const videoCallUrl = `${hostname}/vc/video-call/1o1-${userOneId}-${userTwoId}-meeting`;

  return videoCallUrl;
};

export const MeetingCardItem: React.FC<Props> = ({ meeting, event, mutate }) => {
  const { state: auth } = useAuthContext();
  const { query: _query } = useRouter();
  const isInviter = meeting.inviter === auth?.profile?.id;
  const otherUser = meeting.users_metadata[isInviter ? 'invitee_profile' : 'inviter_profile'];
  const otherUserId = isInviter ? meeting.invitee : meeting.inviter;
  const otherUserAction = isInviter ? meeting.invitee_action : meeting.inviter_action;
  const userStatus = isInviter ? meeting.inviter_action : meeting.invitee_action;
  const [showCancellationModal, toggleCancellationModal] = useReducer(state => !state, false);
  const isWaitingForResponse = !isInviter && userStatus === 'PENDING';
  const meetingStartTime = new Date(meeting.date);
  const meetingEndTime = addHours(meetingStartTime, 1);
  const isInvitationExpired = new Date() > meetingEndTime;
  const canJoinVideoCall = !isInvitationExpired && new Date() > meetingStartTime;
  const canCancel = new Date() < meetingStartTime;
  const videoCallUrl = generateVideoCallUrl(meeting.inviter, meeting.invitee);
  const meetingName = `1-1 meeting with ${otherUser.full_name} - ${event.short_name}`;
  const ref = useRef<React.RefObject<HTMLDivElement> | HTMLDivElement>(null);
  const [element, setElement] = useState<HTMLDivElement>();
  const router = useRouter();
  const [processingAction, setProcessingAction] = useState(false);

  const isMeeting = (status: ScheduledItemStatus): boolean =>
    [meeting.invitee_action, meeting.inviter_action].includes(status);
  const fadeCard =
    isInvitationExpired ||
    isMeeting(ScheduledItemStatus.Canceled) ||
    isMeeting(ScheduledItemStatus.Rejected);

  useEffect(() => {
    setElement(ref.current as HTMLDivElement);
  }, []);

  useEffect(() => {
    const { meetingId } = router.query;

    if (meetingId === meeting._id && element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [element, router.query]);

  const meetingRequestAction = async (
    meetingId: string,
    action: ScheduledItemStatus,
    from: 'invitee' | 'inviter',
  ) => {
    if (processingAction) return;

    setProcessingAction(true);
    await meetingAction(meetingId, action, from);
    const trackingAction = trackingEventPerMeetingAction[action];
    logEvent(`SCHEDULE_MEETING_${trackingAction}_MEETING_REQUEST`, {
      event_id: event.id,
      meeting_id: meetingId,
      target_user: otherUserId,
    });
    // Sometimes the user can trigger a few actions while the previous is being processed
    setTimeout(() => setProcessingAction(false), 1000);
    mutate();
  };

  const acceptMeeting = () =>
    meetingRequestAction(meeting._id, ScheduledItemStatus.Accepted, 'invitee');

  const declineMeeting = () =>
    meetingRequestAction(meeting._id, ScheduledItemStatus.Rejected, 'invitee');

  const cancelMeeting = () =>
    meetingRequestAction(
      meeting._id,
      ScheduledItemStatus.Canceled,
      isInviter ? 'inviter' : 'invitee',
    );

  const deleteMeeting = () =>
    meetingRequestAction(
      meeting._id,
      ScheduledItemStatus.Deleted,
      isInviter ? 'inviter' : 'invitee',
    );

  const renderMeetingActions = () => {
    if (isMeeting(ScheduledItemStatus.Rejected)) {
      return (
        <Flexbox gap="1rem">
          <Text size="body3">
            {`${otherUserAction === 'REJECTED' ? otherUser.full_name : 'You'} declined.`}
          </Text>
          <StyledMenu>
            <StyledMenuButton>
              <MoreButton />
            </StyledMenuButton>
            <Flyout>
              <MenuItem onSelect={deleteMeeting}>Delete Meeting</MenuItem>
            </Flyout>
          </StyledMenu>
        </Flexbox>
      );
    }

    if (isMeeting(ScheduledItemStatus.Canceled)) {
      return (
        <Flexbox gap="1rem">
          <Text size="body3">
            {`${otherUserAction === 'CANCELED' ? otherUser.full_name : 'You'} canceled.`}
          </Text>
          <StyledMenu>
            <StyledMenuButton>
              <MoreButton />
            </StyledMenuButton>
            <Flyout>
              <MenuItem onSelect={deleteMeeting}>Delete Meeting</MenuItem>
            </Flyout>
          </StyledMenu>
        </Flexbox>
      );
    }

    if (meeting.invitee_action === 'ACCEPTED') {
      return (
        <Flexbox gap="1rem" justifyContent="flex-end" alignItems="center">
          {isInvitationExpired ? (
            <Text size="body2_new">The Meeting expired.</Text>
          ) : (
            <CalendarExportDropdown
              session={{
                ...meeting,
                location: 'Online',
                // @ts-ignore
                event,
                name: meetingName,
                description: meeting.description,
                start_date: meetingStartTime.toString(),
                end_date: meetingEndTime.toString(),
              }}
              meetingUrl={videoCallUrl}
              hideLabel
              eventId={_query.eventId as string}
              onExportClick={() =>
                logEvent('SCHEDULE_MEETING_EXPORT_SCHEDULED_MEETING', {
                  event_id: event.id,
                  meeting_id: meeting._id,
                  target_user: otherUserId,
                })
              }
            />
          )}

          <a href={videoCallUrl} target="_blank" rel="noreferrer">
            <Button
              color="primary"
              disabled={!canJoinVideoCall || isInvitationExpired}
              {...(!canJoinVideoCall && {
                disabledTooltipText: 'You will be able to join when the meeting starts',
              })}
              onClick={() =>
                logEvent('SCHEDULE_MEETING_JOIN_SCHEDULED_MEETING', {
                  event_id: event.id,
                  meeting_id: meeting._id,
                  target_user: otherUserId,
                })
              }
            >
              Join
            </Button>
          </a>

          <StyledMenu>
            <StyledMenuButton style={{ color: 'red' }}>
              <MoreButton />
            </StyledMenuButton>
            <Flyout>
              {isInvitationExpired ? (
                <MenuItem onSelect={deleteMeeting}>Delete Meeting</MenuItem>
              ) : (
                <MenuItem disabled={!canCancel} onSelect={toggleCancellationModal}>
                  Cancel Meeting
                </MenuItem>
              )}
            </Flyout>
          </StyledMenu>
        </Flexbox>
      );
    }

    if (userStatus === 'SENT' && isInviter) {
      return (
        <Flexbox gap="1rem">
          <Text size="body3">
            {isInvitationExpired
              ? 'The Meeting expired.'
              : `Waiting for ${meeting.users_metadata.invitee_profile.full_name} response...`}
          </Text>
          <StyledMenu>
            <StyledMenuButton>
              <MoreButton />
            </StyledMenuButton>
            <Flyout>
              {isInvitationExpired ? (
                <MenuItem onSelect={deleteMeeting}>Delete Meeting</MenuItem>
              ) : (
                <MenuItem disabled={!canCancel} onSelect={toggleCancellationModal}>
                  Cancel Meeting
                </MenuItem>
              )}
            </Flyout>
          </StyledMenu>
        </Flexbox>
      );
    }

    if (userStatus === 'PENDING' && !isInviter) {
      return isInvitationExpired ? (
        <Flexbox gap="1rem">
          <Text size="body2_new">The Meeting expired.</Text>
          <StyledMenu>
            <StyledMenuButton>
              <MoreButton />
            </StyledMenuButton>
            <Flyout>
              <MenuItem onSelect={deleteMeeting}>Delete Meeting</MenuItem>
            </Flyout>
          </StyledMenu>
        </Flexbox>
      ) : (
        <Flexbox gap="1rem" alignItems="center">
          <DeclineButton color="red" onClick={declineMeeting}>
            Decline
          </DeclineButton>
          <Button color="primary" onClick={acceptMeeting}>
            Accept
          </Button>
        </Flexbox>
      );
    }

    return <div />;
  };

  return (
    <SessionListCard
      key={meeting._id}
      style={{
        backgroundColor: Theme.color[isWaitingForResponse ? 'sidebarGrey' : 'white'],
      }}
      ref={ref as React.RefObject<HTMLDivElement>}
    >
      <>
        {showCancellationModal ? (
          <CancellationModal
            cancelMeeting={cancelMeeting}
            toggleCancellationModal={toggleCancellationModal}
          />
        ) : null}
      </>
      <Flexbox
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: '0.5rem' }}
      >
        <Text color="grey">MEETINGS</Text>
        {renderMeetingActions()}
      </Flexbox>

      <Flexbox flexDirection="column" gap="0.5rem">
        <Text fontWeight="bold" color={fadeCard ? 'grey' : 'secondaryBrand'}>
          Scheduled Meeting
        </Text>
        {!!meeting.description && (
          <Text
            style={{
              width: '100%',
              overflowWrap: 'anywhere',
              color: fadeCard ? 'grey' : 'secondaryBrand',
            }}
          >
            "{meeting.description}"
          </Text>
        )}
        <Text fontWeight="semiBold" color={fadeCard ? 'grey' : 'secondaryBrand'}>
          {getTimeText({
            start_date: meetingStartTime.toString(),
            end_date: meetingEndTime.toString(),
            showFullTime: true,
            showEndDate: false,
          })}
        </Text>

        <Flexbox alignItems="center">
          <Avatar
            fullName={otherUser.full_name}
            src={otherUser.picture_url}
            size="small"
            style={{
              marginLeft: '0',
              marginRight: '1rem',
              opacity: fadeCard ? '0.65' : '1',
            }}
          />
          <div>
            <Text fontWeight="bold" color={fadeCard ? 'grey' : 'secondaryBrand'}>
              {otherUser.full_name}
            </Text>
            <Text color="grey">{otherUser.organization}</Text>
          </div>
        </Flexbox>
      </Flexbox>
    </SessionListCard>
  );
};
